import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

import { DEFAULT_LOCALE, asLocale, type Locale } from "@quality-field/shared";

import { CATALOG } from "@/i18n/catalog";

/**
 * Translation.
 *
 * ⚠ **French is the default** (Jérémy, 17 Sep 2026): the people the report is built for
 * work in French. English exists because Hugues and the tooling do.
 *
 * Hand-rolled rather than a library, for the same reason the charts are: the whole need is
 * a lookup and a fallback, and a dependency would be more code than the thing it replaces.
 *
 * ⚠ A missing key renders the ENGLISH string, and a key missing from both renders the key
 * itself. Never an empty string: a blank button teaches nobody anything, while `nav.reports`
 * on screen is obviously a bug and gets reported.
 *
 * Where the preference lives, in order:
 *   1. the signed-in user's `languagePref`, so it follows them between machines;
 *   2. `localStorage`, which is all the sign-in screens have — nobody is signed in yet;
 *   3. French.
 */

const STORAGE_KEY = "quality-field.locale";

interface I18nContextValue {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: (key: string, params?: Record<string, string | number>) => string;
}

const I18nContext = createContext<I18nContextValue>({
  locale: DEFAULT_LOCALE,
  setLocale: () => {},
  t: (key) => key,
});

function stored(): Locale | null {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    return value ? asLocale(value) : null;
  } catch {
    // Private windows and blocked site data both throw here. A preference we cannot read
    // is not an error; it is just the default.
    return null;
  }
}

export function I18nProvider({ children, preferred }: { children: ReactNode; preferred?: string | null }) {
  const [locale, setLocaleState] = useState<Locale>(() => stored() ?? DEFAULT_LOCALE);

  // The signed-in user's stored preference wins over this browser's, because it is the one
  // that follows them to another machine. It arrives after the first render — `/auth/me`
  // is a round trip — so this is an effect rather than an initial value.
  useEffect(() => {
    if (preferred) setLocaleState(asLocale(preferred));
  }, [preferred]);

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Remembering the choice is a convenience; the choice itself still applies.
    }
  }, []);

  const t = useCallback(
    (key: string, params?: Record<string, string | number>) => {
      const template = CATALOG[locale]?.[key] ?? CATALOG.en[key] ?? key;
      if (!params) return template;
      return template.replace(/\{(\w+)\}/g, (whole, name: string) =>
        name in params ? String(params[name]) : whole,
      );
    },
    [locale],
  );

  const value = useMemo(() => ({ locale, setLocale, t }), [locale, setLocale, t]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextValue {
  return useContext(I18nContext);
}
